import { useState } from 'react';
import { medName } from '../../../../core/utils/medName';
import { CircleAlert, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

const toDate = value => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = value => {
  if (!value) return '-';
  const date = toDate(value);
  if (!date) return value;
  return `${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}.${date.getFullYear()}`;
};

const md = d => `${d.getMonth() + 1}월 ${d.getDate()}일`;

/* "장기처방 30일 • 약 4월 27일 ~ 5월 26일까지 복용" — 발행일 + total_days (발행일 포함 N일) */
const longTermLabel = (rx, issued, meds) => {
  const days = Number(rx.total_days) || Math.max(0, ...meds.map(m => Number(m.total_days ?? m['총투약일수']) || 0));
  const start = toDate(issued);
  if (!days || !start) return rx.long_term_note || (days ? `장기처방 ${days}일` : null);
  const end = new Date(start);
  end.setDate(end.getDate() + days - 1);
  return `장기처방 ${days}일 • 약 ${md(start)} ~ ${md(end)}까지 복용`;
};

/* Figma 767:28082 row — h28 border-y, 라벨 w100 bg #f4f4f5 px8 / 값 px8, M16 #71717a / #18181b
   full: 위 행 아래에 붙는 단독 행 — 높이 고정 + 선 겹침 방지 */
const InfoCell = ({ label, value, full }) => (
  <div className={`flex h-7 min-w-0 items-center border-y border-zinc-200 bg-white ${full ? '-mt-px w-full shrink-0' : 'flex-1'}`}>
    <span className="flex h-full w-[100px] shrink-0 items-center truncate bg-zinc-100 px-2 text-14 font-medium tracking-[-0.28px] text-zinc-500">{label}</span>
    <span className="min-w-0 flex-1 truncate px-2 text-14 font-medium tracking-[-0.28px] text-zinc-900">{value || '-'}</span>
  </div>
);

const Prescription = ({ rx, patientName }) => {
  const analysis = rx.analysis || {};
  const meds = analysis.medications || rx.medications_resolved || rx.medications || [];
  const disease = analysis.disease_name || rx.diagnosis;
  const diseaseCode = analysis.disease_code || rx.disease_code;
  const diagnosis = disease && diseaseCode ? `${disease} (${diseaseCode})` : disease || diseaseCode || '-';
  const issued = analysis.issue_date || rx.prescription_date || rx.created_at;
  const longTermNote = rx.long_term_note || rx.is_long_term ? longTermLabel(rx, issued, meds) : null;

  return (
    <div className="flex flex-col gap-6">
      {/* 767:28076 — 병원명 SB18, gap12 / Table container gap16 */}
      <section className="flex flex-col gap-3">
        <h3 className="text-18 font-semibold tracking-[-0.36px] text-zinc-900">
          {analysis.hospital_name || rx.hospital_name || '-'}
        </h3>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            <div className="flex">
              <InfoCell label="처방의사" value={analysis.doctor_name || analysis.doctor_license_number || rx.doctor_name} />
              <InfoCell label="발행일" value={formatDate(issued)} />
            </div>
            <InfoCell full label="발행번호" value={analysis.issue_number || rx.prescription_number} />
          </div>
          {/* 767:28097 환자정보 — 제목 SB14 #3f3f46, gap6 */}
          <div className="flex flex-col gap-1.5">
            <h4 className="text-14 font-semibold tracking-[-0.28px] text-zinc-700">환자정보</h4>
            <div className="flex flex-col">
              <div className="flex">
                <InfoCell label="성명" value={analysis.patient_name || rx.patient_name || patientName} />
                <InfoCell label="주민등록번호" value={analysis.resident_number || rx.rrn} />
              </div>
              <InfoCell full label="진단명" value={diagnosis} />
            </div>
          </div>
        </div>
      </section>

      {/* 767:28122 처방의약품 — 제목 SB16 gap10, 표 border-y */}
      <section className="flex flex-col gap-3">
        <h3 className="flex h-5 items-center gap-2.5 text-16 font-semibold tracking-[-0.32px] text-zinc-900">
          <span>처방의약품</span>
          <span>{meds.length}</span>
        </h3>
        <table className="w-full border-y border-zinc-200 text-14 tracking-[-0.28px] text-zinc-700">
          <thead className="h-11 bg-zinc-100 font-medium">
            <tr className="border-b border-zinc-200">
              <th className="px-3 text-left font-medium text-zinc-500">약품명</th>
              <th className="w-[80px] min-w-[60px] px-3 text-center font-medium whitespace-nowrap">1회 투약량</th>
              <th className="w-[80px] min-w-[60px] px-3 text-center font-medium whitespace-nowrap">1일 투여횟수</th>
              <th className="w-[80px] min-w-[60px] px-3 text-center font-medium whitespace-nowrap">총 투약일수</th>
            </tr>
          </thead>
          <tbody>
            {meds.map((med, index) => (
              <tr key={med.edi_code || index} className="h-[60px] border-b border-zinc-200 last:border-b-0">
                <td className="min-w-[220px] px-3 py-1.5">
                  <p className="truncate font-medium text-zinc-900">{medName(med) || '-'}</p>
                  {(med.ingredient || med['성분']) && (
                    <p className="mt-0.5 truncate text-12 tracking-normal text-zinc-700">({med.ingredient || med['성분']})</p>
                  )}
                </td>
                <td className="px-3 text-center">{med.dosage_per_dose ?? med['1회투약량'] ?? '-'}</td>
                <td className="px-3 text-center">{med.frequency_per_day ?? med['1일투약횟수'] ?? '-'}</td>
                <td className="px-3 text-center">{med.total_days ?? med['총투약일수'] ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* 767:28131 alert — bg #fafafa r8 p16, 아이콘 16 #007aff, 텍스트 12 #5081e9 */}
      {(longTermNote || rx.next_schedule_date) && (
        <div className="flex gap-1 rounded-lg bg-[#fafafa] p-4 text-12 text-[#5081e9] shadow-[0_0_22.2px_rgba(67,77,118,0.06)]">
          <CircleAlert size={16} className="shrink-0" fill="#007aff" color="white" />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {longTermNote && <p className="font-semibold">{longTermNote}</p>}
            {rx.next_schedule_date && (
              <ul className="list-disc">
                <li className="ms-[18px]">다음 처방 예정일 {rx.next_schedule_date}</li>
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const PrescriptionTabContent = ({ prescriptions, merged }) => {
  const list = Array.isArray(prescriptions) ? prescriptions : [];
  const [index, setIndex] = useState(0);
  const page = Math.min(index, Math.max(list.length - 1, 0));

  if (list.length === 0) {
    return null;
  }

  return (
    /* 767:28075 — 본문(flex-1) / gap24 / pagination(h36) */
    <div className="flex flex-1 flex-col gap-6">
      <div className="flex-1">
        <Prescription rx={list[page]} patientName={merged?.name || merged?.patient_name} />
      </div>
      <div className="flex h-9 items-center justify-between">
        <span className="pr-2 text-12 font-medium text-zinc-700">페이지 {page + 1} / {list.length}</span>
        <div className="flex gap-2">
          {[
            [ChevronsLeft, () => setIndex(0), page === 0, '첫 페이지'],
            [ChevronLeft, () => setIndex(page - 1), page === 0, '이전 페이지'],
            [ChevronRight, () => setIndex(page + 1), page === list.length - 1, '다음 페이지'],
            [ChevronsRight, () => setIndex(list.length - 1), page === list.length - 1, '마지막 페이지'],
          ].map(([Icon, onClick, disabled, label]) => (
            <button
              key={label}
              type="button"
              onClick={onClick}
              disabled={disabled}
              aria-label={label}
              className="flex size-8 items-center justify-center rounded-lg border border-zinc-200 bg-white disabled:opacity-50"
            >
              <Icon size={16} strokeWidth={1.5} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
